import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Car, Eye, EyeOff, Loader2 } from "lucide-react";

const DEMO_ACCOUNTS = [
  { role: "Admin / Owner", email: "madhawa91@gmail.com", password: "admin123", testId: "demo-role-admin-button" },
  { role: "Sales Manager", email: "sales.manager@autocentral.nz", password: "manager123", testId: "demo-role-manager-button" },
  { role: "Sales Rep", email: "sales.rep@autocentral.nz", password: "sales123", testId: "demo-role-sales-button" },
];

export default function Login() {
  const [email, setEmail] = useState("madhawa91@gmail.com");
  const [password, setPassword] = useState("admin123");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen login-bg flex items-center justify-center px-4">
      <div className="w-full max-w-[980px] grid lg:grid-cols-2 rounded-2xl overflow-hidden shadow-xl bg-white border border-slate-200">
        {/* Left panel */}
        <div className="hidden lg:block relative" style={{ background: "#0B0F17" }}>
          <img
            alt="AutoCentral"
            src="https://images.unsplash.com/photo-1545276133-d91bcba0803c?crop=entropy&cs=srgb&fm=jpg&q=85"
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          <div className="relative h-full p-10 flex flex-col text-white">
            <div className="flex items-center gap-3">
              <img src="/milex-logo.png" alt="Milex" className="w-12 h-12 object-contain" />
              <div>
                <div className="font-bold text-2xl tracking-widest" style={{ fontFamily: "Outfit", color: "#E5B84B" }}>MILEX</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">NZ Dealership DMS</div>
              </div>
            </div>
            <div className="mt-auto">
              <h2 className="text-3xl font-bold leading-tight" style={{ fontFamily: "Outfit" }}>Modern DMS for New Zealand dealerships.</h2>
              <p className="mt-3 text-slate-300 text-sm max-w-xs">VIN & Rego lookup, stock aging, margin tracking, and a deal pipeline that just works.</p>
            </div>
          </div>
        </div>

        {/* Right form */}
        <div className="p-8 sm:p-12">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Welcome back</h1>
            <p className="text-sm text-slate-500 mt-1">Sign in to your dealership account.</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full h-11 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Password</label>
              <div className="relative mt-1.5">
                <input
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 px-3 pr-10 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && <div data-testid="login-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>}

            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign in
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2.5">Quick demo access</div>
            <div className="space-y-1.5">
              {DEMO_ACCOUNTS.map((a) => (
                <button
                  key={a.email}
                  data-testid={a.testId}
                  onClick={() => { setEmail(a.email); setPassword(a.password); }}
                  className="w-full flex items-center justify-between text-left px-3 py-2 rounded-md hover:bg-slate-50 border border-slate-200"
                >
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{a.role}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{a.email}</div>
                  </div>
                  <span className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider">Use</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
